import React from 'react';
import { Link } from 'react-router-dom';
import './Quality.css';
import useReveal from '../hooks/useReveal';
import TokenRevealText from '../components/TokenRevealText';

const qualityProcesses = [
  {
    step: '01',
    title: 'Incoming Material Inspection',
    desc: 'All raw materials are inspected upon receipt to verify compliance with material specifications before use in production.',
  },
  {
    step: '02',
    title: 'In-Process Inspection',
    desc: 'Continuous monitoring and inspection throughout the manufacturing process to catch and correct issues early.',
  },
  {
    step: '03',
    title: 'Mold Validation',
    desc: 'Comprehensive mold validation including dimensional checks, sample runs, and performance testing.',
  },
  {
    step: '04',
    title: 'Sample Testing',
    desc: 'Thorough testing of initial production samples against customer specifications before full production begins.',
  },
  {
    step: '05',
    title: 'Final Product Inspection',
    desc: 'Every finished component undergoes final quality inspection before packing and dispatch to ensure zero defects.',
  },
];

const commitments = [
  { label: 'Precision', value: 'High-accuracy manufacturing' },
  { label: 'Dimensional', value: 'Strict tolerances maintained' },
  { label: 'Material', value: 'Verified material properties' },
  { label: 'Traceability', value: 'Full production records' },
];

const Quality = () => {
  useReveal();
  return (
    <main className="quality-page page-enter">
      {/* Page Hero */}
      <section className="page-hero">
        <div className="container reveal">
          <p className="section-eyebrow-light"><TokenRevealText text="Our Standards" speed="medium" /></p>
          <h1><TokenRevealText text="Quality Assurance" speed="medium" /></h1>
          <p>
            <TokenRevealText
              text="Quality is at the core of every process – from design to delivery."
              speed="medium"
            />
          </p>
        </div>
      </section>

      {/* ===== QUALITY INTRO ===== */}
      <section className="section quality-intro-section">
        <div className="container">
          <div className="grid-2">
            <div className="reveal reveal-left">
              <p className="section-eyebrow"><TokenRevealText text="Our Commitment" speed="medium" /></p>
              <h2 className="section-title"><TokenRevealText text="Built on Quality" speed="medium" /></h2>
              <div className="divider" />
              <p className="body-text">
                <TokenRevealText
                  text={
                    'Quality is at the core of our manufacturing process. We maintain strict quality control standards across design, tooling, and production to ensure that every component we deliver meets or exceeds customer expectations.'
                  }
                  speed="medium"
                />
              </p>
              <p className="body-text">
                <TokenRevealText
                  text={
                    'Our quality philosophy is embedded at every stage of the manufacturing lifecycle – from material selection through to final dispatch. Our experienced quality control team works alongside production to proactively identify and resolve issues.'
                  }
                  speed="medium"
                />
              </p>
              <div className="quality-commitments">
                {commitments.map((c, i) => (
                  <div key={c.label} className={`qc-item reveal reveal-scale reveal-d${(i % 3) + 1}`}>
                    <div>
                      <strong><TokenRevealText text={c.label} speed="medium" /></strong>
                      <span><TokenRevealText text={c.value} speed="medium" /></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="media-card reveal reveal-right" style={{ height: '400px' }}>
              <img
                src="/images/quality-operator.JPG"
                alt="Quality Inspection"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = '/images/C2P2.jpg';
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== QUALITY PROCESS ===== */}
      <section className="section section-alt quality-process-section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <p className="section-eyebrow"><TokenRevealText text="Our Process" speed="medium" /></p>
            <h2 className="section-title centered">
              <TokenRevealText text="Quality Control Process" speed="medium" />
            </h2>
            <div className="divider" style={{ margin: '12px auto 20px' }} />
            <p style={{ color: 'var(--text-gray)', maxWidth: '560px', margin: '0 auto', fontSize: '1rem' }}>
              <TokenRevealText
                text="A systematic, end-to-end quality control process ensures every component meets the highest standards before it reaches our customers."
                speed="medium"
              />
            </p>
          </div>
          <div className="quality-steps">
            {qualityProcesses.map((qp, i) => (
              <div key={qp.step} className={`quality-step reveal reveal-d${i + 1}`}>
                <div className="qs-step-num">{qp.step}</div>
                <div>
                  <h3><TokenRevealText text={qp.title} speed="medium" /></h3>
                  <p><TokenRevealText text={qp.desc} speed="medium" /></p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="quality-cta">
        <div className="container quality-cta-inner">
          <div className="reveal reveal-left">
            <h2><TokenRevealText text="Quality you can trust" speed="medium" /></h2>
            <p>
              <TokenRevealText
                text="Our commitment to quality means zero compromise on every component we manufacture."
                speed="medium"
              />
            </p>
          </div>
          <Link to="/contact" className="btn-primary reveal reveal-right">Let's Connect</Link>
        </div>
      </section>
    </main>
  );
};

export default Quality;